import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { OnboardingContext } from "../modular/OnboardingContext.tsx";
import QuestContext from "../../../../components/QuestWrapper.tsx";

import {
  OnboardingModularProps,
  OnboardingComponents,
  offlineOnboardingModularProps,
} from "../modular/types.ts";
import "../../../../components/Modules/css/input.css";

import General, { extractGradientColors, hexToGlassColor } from "../../../../general.ts";

import "../components/onboarding.css";

import {
  DEFAULT_BRAND_THEME,
  DEFAULT_QUEST_THEME,
} from "../components/tutorial.types";
import OnboardingApi, {
  UiPropsInterface,
} from "../components/OnboardingApi.ts";
import OnboardingThemeManager from "../components/themeManager.ts";
import config from "../../../../config.ts";
import Cookies from "universal-cookie";

// Onboarding Functions
import { metricApi, templateDesign } from "../modular/OnboardingFunctions.ts";
import axios from "axios";
import OnboardingGetPropsFunctions from "../modular/OnboardingGetPropsFunctions.ts";
import { ScreenHeadings } from "../modular/types.ts";
import Header from "../modular/Header";
import Content from "../modular/Content2.tsx";
import Footer from "../modular/Footer";

interface OnboardingComponentsOffline extends OnboardingComponents {
  Header: typeof Header;
  Content: typeof Content;
  Footer: typeof Footer;
}

const OnBoardingOffline: React.FC<offlineOnboardingModularProps> &
  OnboardingComponentsOffline = ({
  answer,
  getAnswers,
  questId: campaignId,
  setAnswer,
  formdata = [],
  BrandTheme = DEFAULT_BRAND_THEME,
  questThemeData = DEFAULT_QUEST_THEME,
  controlBtnType,
  design = [],
  Headers = [],
  loadingTracker,
  multiChoice = "modal1",
  nextBtnText,
  onPageChange,
  progress,
  progressBarMultiLine,
  setLoading = () => {},
  showFooter = true,
  singleChoose,
  styleConfig,
  template,
  children,
  ...props
}) => {
  const { themeConfig } = useContext(QuestContext.Context);
  const progressRef = useRef<HTMLDivElement>(null);
  const [steps, setSteps] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [btnFlag, setButtonFlag] = useState<boolean>(false);
  const [designState, setDesign] = useState<number[][]>([]);
  const [validationData, setValidationData] = useState<DynamicBooleanObject>(
    {}
  );
  const campaignVariationId = '1'
  const apiSecret = ''
  const [headersArray, setHeadersArray] = useState<ScreenHeadings | any>(
    Headers
  );
  const [questionTemplate, setQuestionTemplate] = useState<
    "multi-question" | "single-question" | "single-page"
  >(template || "single-page");
  const [wd, setWd] = useState(0);

  interface DynamicBooleanObject {
    [key: string]: boolean;
  }

  useEffect(() => {
    if (!formdata.length) return;

    let currentQuestions: number[];
    if (questionTemplate === "multi-question") {
      currentQuestions = formdata.map((_, i) => i + 1);
      currentQuestions = currentQuestions.filter(i => 
        designState[currentPage] && 
        Array.isArray(designState[currentPage]) && 
        designState[currentPage].includes(i)
      );
    }
    
     else if (questionTemplate === "single-question") {
      currentQuestions = [currentPage + 1];
    } else {
      currentQuestions = formdata.map((e, i) => i + 1);
    }

    let completedCount = currentQuestions.reduce(
      (count: number, questionIndex: number) => {
        const item = formdata[questionIndex - 1];
        return !item.required ||
          item.type === "LINK_OPEN_READ" ||
          (answer[item.actionId] && answer[item.actionId].length > 0)
          ? count + 1
          : count;
      },
      0
    );

    setButtonFlag(
      currentQuestions.length > 0 && completedCount === currentQuestions.length
    );
  }, [answer, formdata, currentPage]);

  useEffect(() => {
    if (btnFlag) {
      setSteps([...steps, currentPage]);
    } else if (steps.includes(currentPage)) {
      setSteps(steps.filter((step) => step !== currentPage));
    }
  }, [btnFlag, currentPage]);

  useEffect(() => {
    onPageChange?.(currentPage);
  }, [currentPage]);

  const handleUpdate = (e: any, id: string, type: string) => {
    if (type === "check") {
      const newAnswer = e.target.checked
        ? [...(answer[id] || []), e.target.value]
        : answer[id].filter((val: string) => val !== e.target.value);
      setAnswer({ ...answer, [id]: newAnswer });
    } else {
      setAnswer({ ...answer, [id]: e.target.value });
    }
  };

  const ProgressBar = () => {
    useEffect(() => {
      if (progressRef.current && progressRef.current.clientWidth !== wd) {
        setWd(progressRef.current.clientWidth);
      }
    }, []);

    return (
      <div className="q-onb-progress">
        <div
          style={{
            gridTemplateColumns: progress
              ? `repeat(${progress.length}, 1fr)`
              : "",
          }}
          ref={progressRef}
        >
          {progress?.map((prog: string, i: number) => (
            <div key={i}>
              <div
                className="q-onb-progress-comp"
                style={{
                  borderColor: steps.includes(i)
                    ? themeManager.getProgressBarBorderStyles()
                    : i === currentPage
                    ? themeManager.getProgressBarCurrentTabColor()
                    : themeManager.getProgressBarPendingTabColor(),
                }}
              >
                <div
                  style={{
                    maxWidth: `${
                      (wd - (progress.length - 1) * 15) / progress.length - 32
                    }px`,
                    whiteSpace: progressBarMultiLine ? "normal" : "nowrap",
                    overflow: progressBarMultiLine ? "" : "hidden",
                    textOverflow: progressBarMultiLine ? "" : "ellipsis",
                    color: steps.includes(i)
                      ? themeManager.getProgressBarStyles()
                      : i === currentPage
                      ? themeManager.getProgressBarCurrentTabColor()
                      : themeManager.getProgressBarPendingTabColor(),
                  }}
                >
                  {prog}
                </div>
                {steps.includes(i) && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 21 20"
                    fill="none"
                  >
                    <path
                      opacity="0.3"
                      d="M10.8333 18.3333C15.4357 18.3333 19.1667 14.6023 19.1667 9.99996C19.1667 5.39759 15.4357 1.66663 10.8333 1.66663C6.23095 1.66663 2.49999 5.39759 2.49999 9.99996C2.49999 14.6023 6.23095 18.3333 10.8333 18.3333Z"
                      fill={
                        hexToGlassColor(BrandTheme?.primaryColor || questThemeData?.accentColor || BrandTheme?.accentColor || "#098849", 0.5)
                      }
                    />
                    <path
                      d="M14.8074 6.51474C15.1215 6.17828 15.6488 6.1601 15.9853 6.47412C16.3217 6.78815 16.3399 7.31548 16.0259 7.65193L10.1925 13.9019C9.88787 14.2284 9.38003 14.2566 9.041 13.9661L6.12434 11.4661C5.7749 11.1665 5.73443 10.6404 6.03395 10.291C6.33347 9.94157 6.85955 9.9011 7.20899 10.2006L9.51916 12.1808L14.8074 6.51474Z"
                      fill={extractGradientColors(themeManager.getCheckBoxColor())}
                    />
                  </svg>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const validateInputRegex = (validation: boolean, actionId: string) => {
    setValidationData((prev) => ({ ...prev, [actionId]: validation }));
  };

  const checkValidationValues = () => {
    const validCount = Object.values(validationData).filter(Boolean).length;
    return validCount === Object.keys(validationData).length;
  };

  function returnAnswers() {
    if (currentPage < designState.length - 1) {
      setCurrentPage((prev) => prev + 1);
      return;
    }

    const processedAnswer = { ...answer };
    for (const key of Object.keys(processedAnswer)) {
      if (key.includes("/manual") && processedAnswer[key]) {
        const baseId = key.split("/manual")[0];
        const criteria = formdata.find((item) => item.actionId === baseId);
        if (criteria?.manualInput === processedAnswer[baseId]) {
          processedAnswer[baseId] = processedAnswer[key];
        }
      }
    }

    getAnswers?.(processedAnswer);
  }

  const themeManager = useMemo(() => {
    return new OnboardingThemeManager(
      themeConfig,
      styleConfig,
      questThemeData,
      BrandTheme
    );
  }, [themeConfig, styleConfig, questThemeData, BrandTheme]);

  const onboardingGetPropsFunctions = new OnboardingGetPropsFunctions(
    controlBtnType,
    design,
    Headers,
    loadingTracker,
    multiChoice,
    nextBtnText,
    progress,
    progressBarMultiLine,
    showFooter,
    singleChoose,
    template
  );

  useEffect(() => {
    templateDesign({
      template: onboardingGetPropsFunctions.getTemplate(),
      design: onboardingGetPropsFunctions?.getDesign(),
      setDesign,
      formdata,
    });
  }, [formdata]);

  const contextValue = {
    formdata,
    headersArray,
    currentPage,
    setCurrentPage,
    design,
    designState,
    progress,
    progressBarMultiLine,
    ProgressBar,
    template,
    singleChoose,
    multiChoice,
    answer,
    controlBtnType,
    nextBtnText,
    campaignId,
    btnFlag,
    returnAnswers,
    handleUpdate,
    validateInputRegex,
    checkValidationValues,
    themeManager,
    validationData,
    setAnswer,
    onboardingGetPropsFunctions,
    campaignVariationId,
    apiSecret,
    questionTemplate,
    showFooter,
  };

  if(formdata.length == 0) return 


  return (
    <OnboardingContext.Provider value={contextValue}>
      {formdata.length > 0 && (
        <div
          className="q-onb-home"
          style={themeManager.getContainerStyles()}
          {...props}
        >
          <div className="q-onb-ch">
            {children || (
              <>
                <Header />
                <Content />
                <Footer />
              </>
            )}
          </div>
        </div>
      )}
    </OnboardingContext.Provider>
  );
};

OnBoardingOffline.Header = Header;
OnBoardingOffline.Content = Content;
OnBoardingOffline.Footer = Footer;

export default OnBoardingOffline;
